// ============================================
// Protected Route Component
// Supports role-specific login paths
// ============================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * ProtectedRoute - Wraps routes that require authentication
 * @param {ReactNode} children - Child components to render
 * @param {string[]} allowedRoles - Array of roles allowed to access this route
 * @param {string} loginPath - Login URL to redirect unauthenticated users to
 */
const ProtectedRoute = ({ children, allowedRoles = [], loginPath = '/login' }) => {
    const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);
    const location = useLocation();

    // Show loading spinner while checking auth
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#050510] transition-colors">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-bold uppercase tracking-widest">Loading...</p>
                </div>
            </div>
        );
    }

    // Not authenticated → redirect to role-specific login
    if (!isAuthenticated) {
        return <Navigate to={loginPath} state={{ from: location }} replace />;
    }

    // Role check — if roles specified, user must have one of them
    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
        // Redirect to their correct dashboard
        const dashboardRoutes = {
            admin: '/admin',
            dispatcher: '/dispatcher',
            driver: '/driver',
            user: '/user',
            pa: '/pa',
        };
        const redirectTo = dashboardRoutes[user?.role] || '/admin/login';
        return <Navigate to={redirectTo} replace />;
    }

    return children;
};

export default ProtectedRoute;
